import { GENERIC_DISPLAY_DECIMALS } from '@ui/constants/numbersDecimals';
import {
  MaxInputLimitation,
  MinInputLimitation,
  Value,
} from '@ui/features/Trade/types';
import { formatNumber } from '@ui/lib/bignumber';
import { Button } from '@ui/lib/park/ui/button';
import { Text } from '@ui/lib/park/ui/text';
import type { ChainId } from '@ui/types/chain';
import { Nullable, PossibleBigNumber } from '@ui/types/utility';
import BigNumber from 'bignumber.js';
import { isNil, memoize } from 'lodash-es';
import { TFunction } from 'next-i18next';
import { HStack } from 'styled-system/jsx';

export type MaxCollateralAmountError =
  | 'must_lt_leverageMarginNotionalUsd'
  | 'must_lt_maxInputMaxLongOILimit'
  | 'must_lt_maxInputMaxShortOILimit'
  | 'max_oi_limit';

type CollateralAmountError =
  | 'min_usd_value'
  | 'balance_insufficient'
  | 'must_gt_minNotionalUsd'
  | MaxCollateralAmountError;

export const validateCollateralAmount = memoize(
  ({
    token,
    usd,
    balance,
    minInput,
    maxInput,
    isLong,
  }: Value & {
    balance: PossibleBigNumber;
    minInput: MinInputLimitation;
    maxInput: MaxInputLimitation;
    isLong: boolean;
  }): {
    error?: CollateralAmountError;
  } => {
    if (isNil(usd) || isNil(token) || isNil(balance)) {
      return {};
    }

    if (token.gt(balance) || balance.isZero()) {
      return {
        error: 'balance_insufficient',
      };
    }

    const minInputValidationCheckResult = minInputValidationCheck({
      usd,
      minInput,
      token,
    });
    const maxInputValidationCheckResult = maxInputValidationCheck(
      usd,
      maxInput,
      isLong,
    );

    if (
      minInputValidationCheckResult.minUsd &&
      maxInputValidationCheckResult.maxUsd &&
      minInputValidationCheckResult.minUsd.gt(
        maxInputValidationCheckResult.maxUsd,
      )
    ) {
      return {
        error: 'max_oi_limit',
      };
    }
    if (minInputValidationCheckResult.error) {
      return {
        error: minInputValidationCheckResult.error,
      };
    }

    if (maxInputValidationCheckResult?.error) {
      return {
        error: maxInputValidationCheckResult.error,
      };
    }

    return {};
  },
  ({ token, usd, balance, minInput, isLong, maxInput }) =>
    `${isLong}-${token?.toString()}-${usd?.toString()}-${balance?.toString()}-${
      minInput.min10Limit.token
    }-${minInput.min10Limit.usd}-${minInput.minNotionalLimit.token}-${
      minInput.minNotionalLimit.usd
    }-${maxInput.leverageMarginNotionalMaxLimit.token}-${
      maxInput.leverageMarginNotionalMaxLimit.usd
    }-${maxInput.maxInputMaxLongOILimit.token}-${
      maxInput.maxInputMaxLongOILimit.usd
    }-${maxInput.maxInputMaxShortOILimit.token}-${
      maxInput.maxInputMaxShortOILimit.usd
    }`,
);

interface ValidateCollateralAmountParams {
  t: TFunction;
  error: Nullable<CollateralAmountError>;
  leverage: Nullable<number>;
  isInputToken: boolean;
  symbol: Nullable<string>;
  minInput: MinInputLimitation;
  maxInput: MaxInputLimitation;
  chainId: ChainId;
}

export const getCollateralAmountErrorMessage = ({
  t,
  error,
  leverage,
  isInputToken,
  symbol,
  minInput,
  maxInput,
  chainId: _chainId,
}: ValidateCollateralAmountParams) => {
  if (!error) {
    return '';
  }

  switch (error) {
    case 'min_usd_value':
      const { min10Limit } = minInput;

      return t('common.Min collateral {amountOrUsd} for {leverage}x', {
        amountOrUsd: formatPayLabel(isInputToken, symbol, min10Limit),
        leverage: leverage,
      });

    case 'balance_insufficient':
      return (
        <HStack gap={1}>
          <Text>{t('common.Balance insufficient.')}</Text>
          {/* <Button variant="link">{t('common.Replenish now')}</Button> */}
        </HStack>
      );
    case 'must_gt_minNotionalUsd':
      const { minNotionalLimit } = minInput;

      return t(
        'common.Min collateral: {collateralInUsdOrAmount} for {leverage}x',
        {
          collateralInUsdOrAmount: formatPayLabel(
            isInputToken,
            symbol,
            minNotionalLimit,
          ),
          leverage,
        },
      );
    case 'must_lt_leverageMarginNotionalUsd':
      const { leverageMarginNotionalMaxLimit } = maxInput;

      return t('common.Max notional USD reached', {
        collateralInUsdOrAmount: formatPayLabel(
          isInputToken,
          symbol,
          leverageMarginNotionalMaxLimit,
        ),
        leverage,
      });

    case 'must_lt_maxInputMaxLongOILimit':
      const { maxInputMaxLongOILimit } = maxInput;

      return t(
        'common.OI reaching max capacity, please input up to {collateralInUsdOrAmount} for {leverage}x leverage',
        {
          collateralInUsdOrAmount: formatPayLabel(
            isInputToken,
            symbol,
            maxInputMaxLongOILimit,
          ),
          leverage,
        },
      );

    case 'must_lt_maxInputMaxShortOILimit':
      const { maxInputMaxShortOILimit } = maxInput;

      return t(
        'common.OI reaching limit {collateralInUsdOrAmount} {leverage}x',
        {
          collateralInUsdOrAmount: formatPayLabel(
            isInputToken,
            symbol,
            maxInputMaxShortOILimit,
          ),
          leverage,
        },
      );

    case 'max_oi_limit':
      return t('common.Max OI limited');

    default:
      throw new Error('should not have default case');
  }
};

export const formatPayLabel = (
  isInputToken: boolean,
  symbol: Nullable<string>,
  value: Value,
) => {
  if (isInputToken) {
    return `${
      value.token && value.token?.isFinite()
        ? formatNumber(value.token, {
            displayDecimals: GENERIC_DISPLAY_DECIMALS,
            saveDecimalsMode: true,
          })
        : '-'
    } ${symbol}`;
  }

  return `$${
    value.usd
      ? formatNumber(value?.usd, {
          displayDecimals: GENERIC_DISPLAY_DECIMALS,
          saveDecimalsMode: true,
        })
      : '-'
  }`;
};

export const maxInputValidationCheck = (
  usd: BigNumber,
  maxInput: MaxInputLimitation,
  isLong: boolean,
): {
  maxUsd?: BigNumber;
  error?: MaxCollateralAmountError;
} => {
  const {
    leverageMarginNotionalMaxLimit,
    maxInputMaxLongOILimit,
    maxInputMaxShortOILimit,
  } = maxInput;

  if (
    isNil(leverageMarginNotionalMaxLimit.usd) ||
    isNil(maxInputMaxLongOILimit.usd) ||
    isNil(maxInputMaxShortOILimit.usd)
  )
    return {};

  const maxLimitArray = (
    [
      {
        usd: leverageMarginNotionalMaxLimit.usd,
        error: 'must_lt_leverageMarginNotionalUsd',
      },
      isLong
        ? {
            usd: maxInputMaxLongOILimit.usd,
            error: 'must_lt_maxInputMaxLongOILimit',
          }
        : {
            usd: maxInputMaxShortOILimit.usd,
            error: 'must_lt_maxInputMaxShortOILimit',
          },
    ] satisfies {
      error: MaxCollateralAmountError;
      usd: BigNumber;
    }[]
  ).sort((a, b) => a.usd.minus(b.usd).toNumber());

  if (usd.gte(maxLimitArray[0].usd)) {
    return {
      maxUsd: maxLimitArray[0].usd,
      error: maxLimitArray[0].error as MaxCollateralAmountError,
    };
  }

  return {
    maxUsd: maxLimitArray[0].usd,
  };
};
const minInputValidationCheck = ({
  usd,
  minInput,
}: Value & {
  minInput: MinInputLimitation;
}) => {
  if (!usd || !minInput) return {};
  const minLimitArray = [
    ...(isNil(minInput.minNotionalLimit.usd)
      ? []
      : [
          {
            usd: minInput.minNotionalLimit.usd,
            error: 'must_gt_minNotionalUsd',
          },
        ]),
  ].sort((a, b) => b.usd.minus(a.usd).toNumber());

  if (usd.lt(minLimitArray[0]?.usd)) {
    return {
      minUsd: minLimitArray[0]?.usd,
      error: minLimitArray[0].error as CollateralAmountError,
    };
  }

  return {
    minUsd: minLimitArray[0]?.usd,
  };
};
